/**
 * Datasets — the reusable saved-query library. Ports mockups/04-datasets.html to
 * a live page: loads datasets from GET /api/datasets (which also returns header
 * stats), supports search + client-filter pills, and per-row actions (refresh,
 * download CSV, delete).
 *
 * There is no "New Dataset" action here by design. Datasets are produced by
 * Porter ("Save as dataset" inside a map editor) — see the info callout and
 * CLAUDE.md "Porter routing". This page browses/maintains what Porter creates;
 * on a fresh install the table is legitimately empty, so it renders a proper
 * empty state rather than treating zero datasets as an error.
 *
 * Response shape (GET /api/datasets?client_id=&q=&page=&limit=):
 *   { datasets: [{ id, name, client_id, client_name, source_query, dataset_type,
 *                  is_locked, refreshed_at, created_by, created_at, updated_at,
 *                  row_count, used_in: [{ id, name }] }],
 *     total, page, limit,
 *     stats: { dataset_count, total_rows, linked_maps } }
 *
 * Pagination: the table is SERVER-paginated (the full library isn't always
 * loaded), so search + the client-filter pills are also SERVER-side — they narrow
 * the whole library, not just the current page. Any search/filter change resets
 * to page 1. Search is debounced so typing doesn't fire a request per keystroke.
 * The stat cards and "Showing N of M" reflect the whole filtered library (total),
 * not the loaded page.
 *
 * Client pills are populated from GET /api/clients (the shared picker source),
 * not derived from the loaded rows, so every client is filterable even before a
 * dataset is assigned to it. "Unassigned" is offered only when some loaded
 * dataset has no client.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Database,
  RefreshCw,
  Download,
  Trash2,
  MapPin,
  Building2,
  Map as MapIcon,
  LayoutGrid,
  Layers,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import api, { ApiError } from '../utils/api';
import Sidebar from '../components/Sidebar';
import { relativeTime, formatNumber } from '../utils/format';
import { useToast } from '../context/ToastContext';
import './Datasets.css';

// Sentinel client-filter values that aren't real client ids.
const ALL_CLIENTS = '__all__';
const UNASSIGNED = '__unassigned__';

// Datasets per page (server-paginated). Kept modest so the table + pagination
// control stay usable; the server clamps anything larger.
const PAGE_SIZE = 25;

// Debounce (ms) before a search keystroke triggers a server fetch, so typing
// doesn't fire a request per character.
const SEARCH_DEBOUNCE_MS = 300;

// Per-type category badge config: a human label + a leading Lucide icon. Uses a
// NEUTRAL badge with an icon rather than the mock-up's semantic color badges
// (badge-error for "Counties" reads as an error state — pre-dev review S-3). The
// dataset_type enum is zips|cities|counties|tracts|tiered (schema).
const TYPE_META = {
  zips: { label: 'ZIPs', Icon: MapPin },
  cities: { label: 'Cities', Icon: Building2 },
  counties: { label: 'Counties', Icon: MapIcon },
  tracts: { label: 'Tracts', Icon: LayoutGrid },
  tiered: { label: 'Tiered', Icon: Layers },
};

export default function Datasets() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [datasets, setDatasets] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // `query` is the live input value; `debouncedQuery` is what actually drives the
  // server fetch (updated SEARCH_DEBOUNCE_MS after typing stops).
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [clientFilter, setClientFilter] = useState(ALL_CLIENTS);
  const [page, setPage] = useState(1); // 1-based
  // Ids currently mid-action, so we can disable that row's buttons + spin the
  // refresh icon without a full-page loading state.
  const [refreshingId, setRefreshingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Load the client list once on mount for the filter pills (independent of the
  // dataset list; a failure is non-fatal — pills just omit named clients).
  useEffect(() => {
    let cancelled = false;
    api
      .get('/clients')
      .then((payload) => {
        if (!cancelled) setClients(Array.isArray(payload?.clients) ? payload.clients : []);
      })
      .catch(() => {
        /* non-fatal: the pills fall back to All clients / Unassigned only */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Debounce the search box → debouncedQuery. Resetting page to 1 happens in the
  // fetch effect below (any filter/search change lands us on page 1).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  // Whenever the search text or client filter changes, jump back to page 1 — the
  // current page number is meaningless against a different result set.
  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, clientFilter]);

  // Track the latest request so a slow earlier response can't overwrite a newer
  // one (out-of-order responses when the user pages/searches quickly).
  const requestSeq = useRef(0);

  // Fetch a page of datasets from the server for the current filter/search/page.
  // Search + client filter are SERVER-side (they narrow the whole library, not
  // just the loaded page), so this is the single source of the visible rows.
  const loadDatasets = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedQuery) params.set('q', debouncedQuery);
      if (clientFilter !== ALL_CLIENTS && clientFilter !== UNASSIGNED) {
        params.set('client_id', clientFilter);
      } else if (clientFilter === UNASSIGNED) {
        params.set('unassigned', '1');
      }
      params.set('page', String(page));
      params.set('limit', String(PAGE_SIZE));

      const payload = await api.get(`/datasets?${params.toString()}`);
      if (seq !== requestSeq.current) return; // a newer request superseded this one
      setDatasets(Array.isArray(payload?.datasets) ? payload.datasets : []);
      setTotal(Number.isFinite(payload?.total) ? payload.total : 0);
      setStats(payload?.stats || null);
      setLoadError(null);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setLoadError(err.message || 'Could not load datasets.');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [debouncedQuery, clientFilter, page]);

  // Run the fetch on mount and whenever the filter/search/page inputs change.
  useEffect(() => {
    loadDatasets();
  }, [loadDatasets]);

  // Client-filter pills: sourced from GET /api/clients so every client is
  // selectable. "Unassigned" is always offered — it's a valid server-side filter
  // (client_id IS NULL), so it can't be derived from a single page's rows.
  const clientPills = useMemo(() => {
    const pills = [{ key: ALL_CLIENTS, label: 'All clients' }];
    for (const c of clients) pills.push({ key: c.id, label: c.name });
    pills.push({ key: UNASSIGNED, label: 'Unassigned' });
    return pills;
  }, [clients]);

  // Whether any filter/search is narrowing the library right now — used to tell a
  // genuinely-empty library ("no datasets yet") from a filter that matched nothing.
  const isFiltering = debouncedQuery !== '' || clientFilter !== ALL_CLIENTS;

  // Refresh a dataset: re-run its stored query server-side and swap in the fresh
  // row_count + refreshed_at. The server returns the whole refreshed dataset, so
  // merge that back into local state. A 422 means this dataset isn't
  // machine-refreshable (natural-language query with no Porter spec) — surface
  // the server's explanation as an info toast, not a hard error.
  const handleRefresh = async (dataset) => {
    if (refreshingId || deletingId) return;
    setRefreshingId(dataset.id);
    try {
      const result = await api.post(`/datasets/${dataset.id}/refresh`);
      setDatasets((prev) =>
        prev.map((d) =>
          d.id === dataset.id
            ? {
                ...d,
                row_count: result.row_count,
                refreshed_at: result.dataset?.refreshed_at ?? d.refreshed_at,
                updated_at: result.dataset?.updated_at ?? d.updated_at,
              }
            : d
        )
      );
      const msg = result.truncated
        ? `Refreshed "${dataset.name}" — ${formatNumber(result.row_count)} rows (results were capped)`
        : `Refreshed "${dataset.name}" — ${formatNumber(result.row_count)} rows`;
      showToast(msg);
    } catch (err) {
      // 422: well-formed request, but this dataset can't be re-executed.
      if (err instanceof ApiError && err.status === 422) {
        showToast(err.message, { variant: 'info', duration: 5000 });
      } else {
        showToast(err.message || 'Could not refresh dataset.', { variant: 'error' });
      }
    } finally {
      setRefreshingId(null);
    }
  };

  // Download the dataset as CSV. The export endpoint is an authenticated GET that
  // returns a text/csv attachment; a plain anchor navigation carries the auth
  // cookie (credentials are same-origin), so point the browser straight at it and
  // let the Content-Disposition header name the file.
  const handleDownload = (dataset) => {
    // REACT_APP_API_URL defaults to '/api' (see utils/api.js) — same origin, so
    // the HttpOnly auth cookie rides along automatically.
    const base = process.env.REACT_APP_API_URL || '/api';
    window.location.href = `${base}/datasets/${dataset.id}/export.csv`;
  };

  // Delete a dataset after confirmation. Because the list is server-paginated,
  // deleting shifts both the page contents and the total, so we REFETCH rather
  // than splice locally — that keeps the "Showing N of M" count and the page
  // slice authoritative. If we just deleted the last row on a page past the
  // first, step back a page so the user doesn't land on an empty page; otherwise
  // reload the current page in place. Deleting a dataset unlinks (but does not
  // delete) any map group built from it — that group survives as a snapshot
  // (decision 4).
  const handleDelete = async (dataset) => {
    if (refreshingId || deletingId) return;
    const usedCount = dataset.used_in?.length || 0;
    const warning = usedCount
      ? `Delete "${dataset.name}"? It's used in ${usedCount} map${usedCount === 1 ? '' : 's'} — those groups keep their current areas but lose the refresh link. This can't be undone.`
      : `Delete "${dataset.name}"? This can't be undone.`;
    if (!window.confirm(warning)) return;

    setDeletingId(dataset.id);
    try {
      await api.delete(`/datasets/${dataset.id}`);
      showToast(`Deleted "${dataset.name}"`);
      if (datasets.length === 1 && page > 1) {
        // Removed the only row on a non-first page → the page-change effect
        // refetches; setPage triggers loadDatasets via its dependency.
        setPage((p) => p - 1);
      } else {
        await loadDatasets();
      }
    } catch (err) {
      showToast(err.message || 'Could not delete dataset.', { variant: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="main">
        <div className="page">
          <div className="page-header-row">
            <header className="app-page-header" style={{ border: 'none', padding: 0, margin: 0 }}>
              <h1 className="app-page-header-title">Datasets</h1>
              <p className="app-page-header-subtitle">
                Named query results — ZIP lists, city rankings, and population pulls — reusable across maps
              </p>
            </header>
            <div className="page-actions">
              <div className="search-wrap">
                <Search />
                <input
                  className="input"
                  type="text"
                  placeholder="Search datasets…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="pills-row">
            {clientPills.map((pill) => (
              <button
                key={pill.key}
                className={`pill${clientFilter === pill.key ? ' is-active' : ''}`}
                onClick={() => setClientFilter(pill.key)}
              >
                {pill.label}
              </button>
            ))}
          </div>

          <div className="stats-row">
            <StatCard label="Datasets" value={stats ? stats.dataset_count : null} loading={loading} />
            <StatCard label="Total rows" value={stats ? stats.total_rows : null} loading={loading} />
            <StatCard label="Linked maps" value={stats ? stats.linked_maps : null} loading={loading} />
          </div>

          <div className="callout callout-info datasets-callout">
            <div>
              <div className="callout-title">Datasets come from Porter</div>
              Ask Porter for data inside any map editor and hit “Save as dataset.” Each dataset
              remembers its source query — refresh any time to pull the latest boundaries and Census
              figures, and every map using it updates.
            </div>
          </div>

          {/* Skeleton only on a load that has nothing to show yet (first load or a
              filter change that emptied the view). Page-to-page refetches keep
              the existing table visible (dimmed via aria-busy) rather than
              flashing the skeleton. */}
          {loading && datasets.length === 0 && !loadError && <DatasetsSkeleton />}

          {!loading && loadError && (
            <div className="callout callout-error" role="alert">
              <div>
                <div className="callout-title">Couldn’t load datasets</div>
                {loadError}
              </div>
            </div>
          )}

          {/* Genuinely-empty library (no datasets at all, no filter active). */}
          {!loading && !loadError && total === 0 && !isFiltering && <DatasetsEmpty />}

          {/* The table: rows on the page, or a filter/search that matched nothing
              (total 0 while filtering → the table shows its inline "no matches"
              row and the footer count). */}
          {!loadError && (datasets.length > 0 || (total === 0 && isFiltering)) && (
            <DatasetsTable
              datasets={datasets}
              total={total}
              page={page}
              totalPages={totalPages}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              busy={loading}
              filtered={isFiltering && datasets.length === 0}
              refreshingId={refreshingId}
              deletingId={deletingId}
              onRefresh={handleRefresh}
              onDownload={handleDownload}
              onDelete={handleDelete}
              onOpenMap={(mapId) => navigate(`/maps/${mapId}`)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Stat card ──────────────────────────────────────────────────────────────
function StatCard({ label, value, loading }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      {loading ? (
        <div className="skeleton skeleton-line w-40" style={{ height: 22, marginTop: 4 }} />
      ) : (
        <div className="stat-value">{value == null ? '—' : formatNumber(value)}</div>
      )}
    </div>
  );
}

// ─── Type badge (neutral badge + leading icon, per review S-3) ────────────────
function TypeBadge({ type }) {
  const meta = TYPE_META[type];
  if (!meta) {
    // Unknown/absent type — show a plain dash badge rather than nothing.
    return <span className="badge">—</span>;
  }
  const { label, Icon } = meta;
  return (
    <span className="badge badge-type">
      <Icon size={12} /> {label}
    </span>
  );
}

// ─── Datasets table ───────────────────────────────────────────────────────────
function DatasetsTable({
  datasets,
  total,
  page,
  totalPages,
  pageSize,
  onPageChange,
  busy,
  filtered,
  refreshingId,
  deletingId,
  onRefresh,
  onDownload,
  onDelete,
  onOpenMap,
}) {
  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="table-wrap" style={{ border: 'none' }} aria-busy={busy || undefined}>
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Client</th>
              <th>Type</th>
              <th style={{ textAlign: 'right' }}>Rows</th>
              <th>Used in</th>
              <th>Updated</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {datasets.map((d) => {
              const busy = refreshingId === d.id || deletingId === d.id;
              return (
                <tr key={d.id}>
                  <td>
                    <div className="ds-name">
                      <Database size={13} /> {d.name || 'Untitled dataset'}
                    </div>
                    {d.source_query && <div className="ds-source">{d.source_query}</div>}
                  </td>
                  <td>
                    <span className="badge">{d.client_name || '—'}</span>
                  </td>
                  <td>
                    <TypeBadge type={d.dataset_type} />
                  </td>
                  <td style={{ textAlign: 'right' }}>{formatNumber(d.row_count ?? 0)}</td>
                  <td>
                    <UsedInChips usedIn={d.used_in} onOpenMap={onOpenMap} />
                  </td>
                  <td>{relativeTime(d.refreshed_at || d.updated_at)}</td>
                  <td>
                    <div className="row-actions">
                      <button
                        className="btn-icon"
                        title="Refresh from source query"
                        onClick={() => onRefresh(d)}
                        disabled={busy}
                      >
                        <RefreshCw size={13} className={refreshingId === d.id ? 'spin' : ''} />
                      </button>
                      <button
                        className="btn-icon"
                        title="Download CSV"
                        onClick={() => onDownload(d)}
                        disabled={busy}
                      >
                        <Download size={13} />
                      </button>
                      <button
                        className="btn-icon"
                        title="Delete dataset"
                        onClick={() => onDelete(d)}
                        disabled={busy}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}

            {filtered && (
              <tr>
                <td colSpan={7} className="datasets-empty-inline">
                  No datasets match the current filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="table-footer">
        <span>
          {total === 0
            ? 'No datasets'
            : `Showing ${datasets.length} of ${formatNumber(total)} dataset${total === 1 ? '' : 's'}`}
        </span>
        {totalPages > 1 && (
          <Pagination page={page} totalPages={totalPages} onPageChange={onPageChange} busy={busy} />
        )}
      </div>
    </div>
  );
}

// ─── Pagination control (design-system .pagination / .pagination-btn) ─────────
// Prev / numbered pages / next. Numbers use a compact window around the current
// page so a large library doesn't render dozens of buttons; the ends (page 1 and
// the last page) are always reachable, with an ellipsis where the window skips.
function Pagination({ page, totalPages, onPageChange, busy }) {
  const pages = pageWindow(page, totalPages);
  const go = (p) => {
    if (busy || p === page || p < 1 || p > totalPages) return;
    onPageChange(p);
  };
  return (
    <div className="pagination">
      <button
        className="pagination-btn"
        onClick={() => go(page - 1)}
        disabled={busy || page <= 1}
        aria-label="Previous page"
        title="Previous page"
      >
        <ChevronLeft size={14} />
      </button>
      {pages.map((p, i) =>
        p === '…' ? (
          <span key={`gap-${i}`} className="pagination-gap" aria-hidden="true">
            …
          </span>
        ) : (
          <button
            key={p}
            className={`pagination-btn${p === page ? ' is-active' : ''}`}
            onClick={() => go(p)}
            disabled={busy}
            aria-label={`Page ${p}`}
            aria-current={p === page ? 'page' : undefined}
          >
            {p}
          </button>
        )
      )}
      <button
        className="pagination-btn"
        onClick={() => go(page + 1)}
        disabled={busy || page >= totalPages}
        aria-label="Next page"
        title="Next page"
      >
        <ChevronRight size={14} />
      </button>
    </div>
  );
}

/**
 * Build the page-number list for the pagination control: always page 1 and the
 * last page, plus a window of pages around the current one, with '…' sentinels
 * where the sequence skips. Keeps the control compact for large libraries.
 *
 * @param {number} page - current 1-based page
 * @param {number} totalPages
 * @returns {Array<number|'…'>}
 */
function pageWindow(page, totalPages) {
  // Small libraries: just list every page (no ellipsis needed).
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const out = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(totalPages - 1, page + 1);
  if (start > 2) out.push('…');
  for (let p = start; p <= end; p++) out.push(p);
  if (end < totalPages - 1) out.push('…');
  out.push(totalPages);
  return out;
}

// "Used in" chips — the maps referencing a dataset. Each chip navigates into that
// map's editor. A dash chip stands in when a dataset isn't used anywhere yet.
function UsedInChips({ usedIn, onOpenMap }) {
  if (!usedIn || usedIn.length === 0) {
    return <span className="item-chip item-chip-empty">—</span>;
  }
  return (
    <div className="used-chips">
      {usedIn.map((m) => (
        <button
          key={m.id}
          className="item-chip item-chip-link"
          title={`Open “${m.name}”`}
          onClick={() => onOpenMap(m.id)}
        >
          {m.name}
        </button>
      ))}
    </div>
  );
}

// ─── Empty state (no datasets at all) ─────────────────────────────────────────
function DatasetsEmpty() {
  return (
    <div className="card">
      <div className="empty-state">
        <div className="empty-state-icon" aria-hidden="true">
          <Database size={28} />
        </div>
        <h3>No datasets yet</h3>
        <p>
          Datasets are created from Porter results inside a map’s editor — ask Porter for data
          (a ZIP list, a city ranking, a population pull) and hit “Save as dataset.” Saved datasets
          show up here, ready to reuse across maps.
        </p>
      </div>
    </div>
  );
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────
function DatasetsSkeleton() {
  return (
    <div className="card" style={{ padding: 0 }} aria-hidden="true">
      <div className="table-wrap" style={{ border: 'none' }}>
        <table className="table">
          <tbody>
            {Array.from({ length: 5 }).map((_, i) => (
              <tr key={i}>
                <td>
                  <div className="skeleton skeleton-line w-60" />
                  <div className="skeleton skeleton-line w-40" style={{ marginTop: 6 }} />
                </td>
                <td><div className="skeleton skeleton-line w-70" /></td>
                <td><div className="skeleton skeleton-line w-60" /></td>
                <td><div className="skeleton skeleton-line w-40" /></td>
                <td><div className="skeleton skeleton-line w-70" /></td>
                <td><div className="skeleton skeleton-line w-60" /></td>
                <td><div className="skeleton skeleton-line w-40" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
